import { useState } from "react";
import { MAX_BESCHRIFTUNG } from "../shared/personen.ts";
import type { BildAusListe } from "./lib/api.ts";
import Figurbild, { figurBreite } from "./Figurbild.tsx";
import Personenzahl, { FRAGE_PERSONEN } from "./Personenzahl.tsx";
import { HINWEIS_ZAHL_FEHLT, startwert } from "./lib/personenwahl.ts";
import { HINWEIS_ZU_KLEIN } from "./lib/vorschau.ts";

/**
 * Schritt 5 aus `PROTOTYP-PLAN.md`: die sichtbare Zuordnung Bild ↔ Person (AB § 1.3).
 *
 * Hier steht nebeneinander, was in der Reihe getrennt ist: **die Figur** — der Ausschnitt,
 * der später gemalt wird — und **das Foto**, aus dem sie kommt. Dazu die beiden Angaben,
 * die zum Element gehören und nicht zu seiner Lage: wie viele Personen es zeigt
 * (Pflichtfeld, ARCHITEKTUR.md 6.2) und wie sie heißen (freier Text, optional).
 *
 * **Warum ein eigener Bildschirm und keine weitere Zeile in der Werkzeugleiste:** die
 * Leiste liegt fest am unteren Rand, und ihre Höhe geht der Bodenlinie verloren. Am
 * 24.09.2026 wurde genau dafür die Streifenhöhe festgenagelt; zwei zusätzliche Zeilen
 * hätten die Linie auf einem 667-px-Bildschirm wieder aus dem Bild geschoben. Dazu
 * kommt das Textfeld: eine Bildschirmtastatur unter einer `fixed`-Leiste ist auf iOS
 * eine Wette, die niemand hier messen kann — auf einem eigenen, scrollbaren Bildschirm
 * gibt es die Wette nicht.
 *
 * Jeder Text ist deutsch, auch jeder Hinweis (`CLAUDE.md`).
 */

/** Höhe der kleinen Figur neben dem Foto. Groß genug, um eine Person zu erkennen, klein
 *  genug, dass Figur und Foto zusammen auf einen Telefonbildschirm passen. */
const FIGUR_HOEHE_PX = 96;

interface Props {
  bild: BildAusListe;
  /** „Figur 3" — dieselbe Zählung wie in der Reihe, damit klar ist, welche gemeint ist. */
  nummer: number;
  /** Speichert und schließt. Die Beschriftung geht ungereinigt hinaus; der Server putzt
   *  sie und gibt zurück, was wirklich in der Zeile steht (`src/lib/api.ts`). */
  onSpeichern: (anzahl: number, beschriftung: string) => void;
  /** Speichert und geht von hier aus ins Umfahren — der Weg, den vorher die
   *  Werkzeugleiste trug. Er gehört hierher: hier liegt das Foto, um das es geht. */
  onUmfahren: (anzahl: number, beschriftung: string) => void;
  /** Das Bild ganz aus dem Projekt nehmen. Schließt diesen Bildschirm; Personenzahl und
   *  Name gehen dabei absichtlich **nicht** mit raus — was gleich weg ist, muss man nicht
   *  vorher speichern. */
  onEntfernen: () => void;
}

export default function Zuordnung({ bild, nummer, onSpeichern, onUmfahren, onEntfernen }: Props) {
  /** Dieselbe Pflichtwahl wie im Umfahren (`src/lib/personenwahl.ts`). **Seit dem
   *  14.10.2026** (step-review vom 08.10.): vorher begann dieser Bildschirm mit
   *  `bild.personen`, und für ein Bild ohne Umriss ist das die Datenbank-Vorgabe 1 —
   *  sie stand als gewählt da, und „Fertig" speicherte sie, ohne dass jemand gezählt hat. */
  const [anzahl, setAnzahl] = useState<number | null>(() => startwert(bild));
  /** Tipp auf „Fertig" ohne Zahl. Der Knopf bleibt tippbar, damit der Tipp nicht stumm
   *  bleibt — dieselbe Abwägung wie beim grauen „Weiter" im Umfahren. */
  const [zahlFehlt, setZahlFehlt] = useState(false);
  const [beschriftung, setBeschriftung] = useState(bild.beschriftung);
  /** Entfernen fragt einmal nach. **Kein `window.confirm`:** dessen Knöpfe beschriftet das
   *  Betriebssystem, und auf einem englisch eingestellten Telefon stünde „OK/Cancel" vor
   *  Annas Kundin (`CLAUDE.md`). Die Rückfrage steht deshalb in der Seite. */
  const [fragtNach, setFragtNach] = useState(false);

  return (
    <div className="fixed inset-0 z-20 flex flex-col overflow-y-auto bg-neutral-900">
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 pt-4 pb-6">
        <h2 className="text-center text-sm font-medium text-neutral-100">Bild {String(nummer)}</h2>

        {/* Das ganze Foto, nicht der Ausschnitt: es beantwortet die Frage „woher kommt
            dieses Bild?". `object-contain` ist hier ausdrücklich in Ordnung — anders als
            in `src/Umfahren.tsx` liegt auf diesem Bild kein `clip-path`, dessen Prozente
            gegen die Elementbox rechnen würden. */}
        <img
          src={bild.vorschau}
          alt=""
          draggable={false}
          className="max-h-[38vh] w-full rounded-lg object-contain"
        />

        {/* Der Auflösungshinweis steht hier und nicht mehr über der ganzen Seite
            (29.09.2026, aus Annas Test). Hier ist die Frage „welches Bild?" beantwortet,
            bevor sie entsteht: das Foto liegt direkt darüber. In der Reihe trägt die
            Kachel nur ein Zeichen — dort ist eine Figur im Mittel 47 px breit. */}
        {bild.zuKlein && (
          <p className="rounded-lg bg-amber-100 px-3 py-2 text-sm text-amber-900">
            {HINWEIS_ZU_KLEIN}
          </p>
        )}

        <div className="flex items-center gap-3">
          <span
            className="block shrink-0"
            style={{ height: FIGUR_HOEHE_PX, width: figurBreite(bild, FIGUR_HOEHE_PX) }}
          >
            <Figurbild bild={bild} />
          </span>
          <p className="text-sm text-neutral-300">
            So steht dieses Bild in der Reihe — der Teil des Fotos, den du umfahren hast.
          </p>
        </div>

        <Personenzahl
          wert={anzahl}
          onAendern={(wert) => {
            setAnzahl(wert);
            setZahlFehlt(false);
          }}
          frage={FRAGE_PERSONEN}
          betont={zahlFehlt}
        />
        {zahlFehlt && (
          <p role="alert" className="text-sm text-amber-300">
            {HINWEIS_ZAHL_FEHLT}
          </p>
        )}

        <label className="flex flex-col gap-1 text-sm text-neutral-200">
          Name (freiwillig)
          <input
            type="text"
            value={beschriftung}
            maxLength={MAX_BESCHRIFTUNG}
            onChange={(event) => {
              setBeschriftung(event.target.value);
            }}
            placeholder="Zum Beispiel: Oma Erna"
            className="min-h-11 rounded-xl border border-neutral-500 bg-neutral-800 px-3 text-base text-neutral-100 placeholder:text-neutral-500"
          />
          <span className="text-neutral-400">
            Hilft dir und Anna, die Bilder später auseinanderzuhalten.
          </span>
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              // Ohne Zahl geht es trotzdem ins Umfahren — dort wird sie ohnehin verlangt.
              // Gespeichert wird dann die Zahl, die schon in der Zeile steht: sie bleibt
              // ungewählt, solange kein Umriss da ist (`startwert`).
              onUmfahren(anzahl ?? bild.personen, beschriftung);
            }}
            className="min-h-14 flex-1 rounded-xl border border-neutral-400 px-4 text-base font-medium text-neutral-100"
          >
            Umriss ändern
          </button>
          <button
            type="button"
            aria-disabled={anzahl === null}
            onClick={() => {
              if (anzahl === null) {
                setZahlFehlt(true);
                return;
              }
              onSpeichern(anzahl, beschriftung);
            }}
            className={`min-h-14 flex-1 rounded-xl px-4 text-base font-medium ${
              anzahl === null ? "bg-neutral-600 text-neutral-300" : "bg-white text-neutral-900"
            }`}
          >
            Fertig
          </button>
        </div>

        {/* Entfernen steht **unter** einer Trennlinie und nicht neben „Fertig" (Annas
            Test, 29.09.2026). Es ist der einzige Handgriff auf diesem Bildschirm, der
            sich nicht zurücknehmen lässt — er gehört deshalb nicht in dieselbe Reihe wie
            die beiden, die man ständig benutzt. Hier ist er trotzdem richtig: dieser
            Bildschirm zeigt das Foto groß, und nur hier ist zweifelsfrei zu sehen,
            **welches** Bild gleich verschwindet. In der Werkzeugleiste der Reihe wäre es
            ein vierter Knopf gewesen, und der schöbe die Bodenlinie aus dem Bild
            (`src/Reihe.tsx`). */}
        <div className="mt-2 border-t border-neutral-700 pt-4">
          {fragtNach ? (
            <div className="flex flex-col gap-3">
              <p role="alert" className="text-sm text-neutral-200">
                Dieses Bild verschwindet aus deiner Reihe. Zurückholen geht nicht — du müsstest das
                Foto noch einmal hochladen.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFragtNach(false);
                  }}
                  className="min-h-14 flex-1 rounded-xl border border-neutral-400 px-4 text-base font-medium text-neutral-100"
                >
                  Behalten
                </button>
                <button
                  type="button"
                  onClick={onEntfernen}
                  className="min-h-14 flex-1 rounded-xl bg-red-700 px-4 text-base font-medium text-white"
                >
                  Ja, entfernen
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setFragtNach(true);
              }}
              className="min-h-14 rounded-xl px-2 text-base font-medium text-red-300 underline"
            >
              Bild entfernen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
